import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';

/**
 * ════════════════════════════════════════════════════════════════════════════
 * A VERSÃO DO PORTAL SÃO DOIS NÚMEROS, E ELES DIVERGEM DE PROPÓSITO
 * ════════════════════════════════════════════════════════════════════════════
 *
 *   web    — o bundle compilado, que muda a cada `publicar-web.sh`
 *   nativo — o `CFBundleVersion` do Info.plist, que só muda quando o Mac compila
 *
 * No navegador só existe o primeiro. No aplicativo existem os dois, e eles NÃO
 * andam juntos: o app carrega um bundle CONGELADO dentro do binário. Um
 * professor no aplicativo pode estar com o web de duas semanas atrás e o nativo
 * de ontem.
 *
 * Reportar só um dos dois torna metade dos relatos do aplicativo não
 * investigáveis — a pergunta "isso já estava corrigido?" não tem resposta sem
 * saber QUAL das duas versões ele carrega.
 */
export interface VersaoDoPortal {
  /** Carimbo do build web: SHA curto, `+` se a árvore estava suja, e a data. */
  web: string;
  /** `CFBundleVersion` do aplicativo. Nulo no navegador, onde não existe. */
  nativo: string | null;
  plataforma: 'web' | 'ios' | 'android';
}

/*
 * `App.getInfo()` é assíncrono e só responde em plataforma nativa. Quem precisa
 * da versão — o limite de erro, o formulário de relato — precisa dela na hora,
 * e nem sempre pode esperar por uma promessa.
 *
 * Então ela é resolvida UMA vez, na subida, e fica em cache. Antes de resolver,
 * `nativo` é nulo — que é o mesmo valor do navegador, e portanto não mente:
 * "não sei" e "não existe" produzem o mesmo relato incompleto, e nenhum dos
 * dois inventa um número.
 */
let nativoEmCache: string | null = null;

export async function resolverVersaoNativa(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const info = await App.getInfo();
    nativoEmCache = info.build || info.version || null;
  } catch {
    nativoEmCache = null;
  }
}

/** Leitura síncrona. Ver a nota sobre o cache acima. */
export function lerVersao(): VersaoDoPortal {
  return {
    web: typeof __VERSAO_WEB__ === 'string' ? __VERSAO_WEB__ : 'desconhecida',
    nativo: nativoEmCache,
    plataforma: Capacitor.getPlatform() as VersaoDoPortal['plataforma'],
  };
}

/** Uma linha para mostrar a uma pessoa. */
export function versaoEmUmaLinha(): string {
  const v = lerVersao();
  return v.nativo ? `${v.plataforma} · web ${v.web} · app ${v.nativo}` : `${v.plataforma} · web ${v.web}`;
}

/**
 * "android/5", "ios/73" — ou nulo (navegador, ou versão nativa ainda não resolvida). Vai ao servidor ao abrir a prova
 * (`utils/api.ts`; o modo prova da 1.4). `resolverVersaoNativa` roda na subida (`main.tsx`), antes de qualquer prova.
 */
export function ambienteDoAplicativo(): string | null {
  const p = Capacitor.getPlatform();
  if ((p !== 'android' && p !== 'ios') || !nativoEmCache || !/^\d+$/.test(nativoEmCache)) return null;
  return `${p}/${nativoEmCache}`;
}

